import React from 'react';
import { useState } from 'react';
import { TIER_OPTIONS, TEMPLATE_OPTIONS } from '../utils/constants';

export function AssetCreationFlow({ 
  projectId, 
  availableAssets = {},
  onGenerateAsset,
  busy = false 
}) {
  const [selectedType, setSelectedType] = useState('audio');
  const [tier, setTier] = useState('loudest');
  const [template, setTemplate] = useState('dynamic');
  const [specificContext, setSpecificContext] = useState('');
  const [prompt, setPrompt] = useState('');

  const hasAudio = availableAssets.audio?.length > 0;
  const hasImage = availableAssets.images?.length > 0;

  const assetTypes = [
    { value: 'audio', label: '🎵 Voice', requires: [], description: 'Generate voiceover' },
    { value: 'image', label: '🖼️ Image', requires: [], description: 'Generate image' },
    { value: 'video', label: '🎬 Video', requires: ['audio', 'image'], description: 'Generate video from assets' }
  ];

  const canGenerate = (type) => {
    const assetType = assetTypes.find(t => t.value === type);
    if (!assetType) return false;
    
    return assetType.requires.every(req => {
      if (req === 'audio') return hasAudio;
      if (req === 'image') return hasImage;
      return true;
    });
  };

  const handleGenerate = () => {
    if (!canGenerate(selectedType)) return;
    
    const payload = {
      type: selectedType,
      tier,
      template: selectedType === 'video' ? template : undefined,
      specificContext: specificContext || undefined,
      chosenPrompt: prompt || undefined
    };
    
    onGenerateAsset(payload);
  };

  return (
    <div style={{
      background: '#111827',
      border: '1px solid #1f2937',
      borderRadius: 12,
      padding: 20
    }}>
      <h3 style={{ color: '#f9fafb', margin: '0 0 16px 0', fontSize: 18 }}>
        Create New Asset
      </h3>
      
      {/* Asset Type Selection */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {assetTypes.map(type => {
          const isAvailable = canGenerate(type.value);
          return (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              disabled={!isAvailable}
              style={{
                flex: 1,
                padding: '12px 8px',
                border: '1px solid',
                borderColor: selectedType === type.value ? '#ff5a1f' : '#374151',
                background: selectedType === type.value ? '#ff5a1f' : '#1f2937',
                color: isAvailable ? '#f9fafb' : '#6b7280',
                borderRadius: 8,
                cursor: isAvailable ? 'pointer' : 'not-allowed',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>{type.label}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{type.description}</div>
              {!isAvailable && (
                <div style={{ fontSize: 10, color: '#ef4444', marginTop: 4 }}>
                  Missing: {type.requires.filter(r => !availableAssets[r]?.length).join(', ')}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Settings */}
      <div style={{ display: 'grid', gap: 12 }}>
        {selectedType === 'video' && (
          <>
            <div>
              <label style={{ color: '#9ca3af', fontSize: 12 }}>Quality Tier</label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0f1115',
                  color: '#e5e7eb',
                  border: '1px solid #374151',
                  borderRadius: 6,
                  padding: '8px'
                }}
              >
                {TIER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ color: '#9ca3af', fontSize: 12 }}>Template</label>
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0f1115',
                  color: '#e5e7eb',
                  border: '1px solid #374151',
                  borderRadius: 6,
                  padding: '8px'
                }}
              >
                {TEMPLATE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </>
        )}
        
        <div>
          <label style={{ color: '#9ca3af', fontSize: 12 }}>Specific Context</label>
          <textarea
            value={specificContext}
            onChange={(e) => setSpecificContext(e.target.value)}
            placeholder={`Add specific context for ${selectedType} generation...`}
            rows={3}
            style={{
              width: '100%',
              background: '#0f1115',
              color: '#e5e7eb',
              border: '1px solid #374151',
              borderRadius: 6,
              padding: '8px',
              resize: 'vertical'
            }}
          />
        </div>
        
        <div>
          <label style={{ color: '#9ca3af', fontSize: 12 }}>Prompt (optional)</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Or use a specific prompt..."
            rows={2}
            style={{
              width: '100%',
              background: '#0f1115',
              color: '#e5e7eb',
              border: '1px solid #374151',
              borderRadius: 6,
              padding: '8px',
              resize: 'vertical'
            }}
          />
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={busy || !canGenerate(selectedType)}
        style={{
          width: '100%',
          background: canGenerate(selectedType) ? '#ff5a1f' : '#374151',
          color: canGenerate(selectedType) ? '#fff' : '#6b7280',
          border: 0,
          borderRadius: 8,
          padding: '12px',
          marginTop: 16,
          cursor: canGenerate(selectedType) && !busy ? 'pointer' : 'not-allowed',
          fontSize: 16,
          fontWeight: 600
        }}
      >
        {busy ? 'Generating...' : `Generate ${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}`}
      </button>
    </div>
  );
}
